/* eslint-disable no-param-reassign */
import Fuse from 'fuse.js';
import _ from 'lodash';

const THRESHOLD = 0.4;
// Matches will be ignored if they occur after `STRING_LENGTH_MAX` characters
const STRING_LENGTH_MAX = 300;
const DISTANCE = STRING_LENGTH_MAX / THRESHOLD;

type LocalEntitySearchType = 'group' | 'user' | 'topic' | 'post';

type BaseData = { id: string };
type GroupData = BaseData & { name: string };
type UserData = BaseData & { name: string };
type TopicData = BaseData & {
  name: string;
  score?: number | null;
  ancestors?: Omit<TopicData, 'ancestors'>[];
};

type PostData = BaseData & {
  archivedAt?: string | null;
  title: string;
  topics?: TopicData[];
};

type BaseEntity<Data extends BaseData, T extends LocalEntitySearchType> = {
  id: string;
  name: string;
  score: number;
  matches: readonly Fuse.FuseResultMatch[];
  secondary?: React.ReactNode;
} & { type: T; object: Data } & { [key in T]: Data };

export type GroupEntity<G extends GroupData = GroupData> = BaseEntity<
  G,
  'group'
>;
export type PostEntity<P extends PostData = PostData> = BaseEntity<P, 'post'>;
export type TopicEntity<T extends TopicData = TopicData> = BaseEntity<
  T,
  'topic'
>;
export type UserEntity<U extends UserData = UserData> = BaseEntity<U, 'user'>;

type BaseSearchConfig<D extends BaseData> = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: (obj: D) => any;
  filter?: (obj: D) => boolean;
  objects: D[];
  sortBy?: (l: D, r: D) => number;
};

export type LocalEntitySearchConfig<
  G extends GroupData = GroupData,
  P extends PostData = PostData,
  T extends TopicData = TopicData,
  U extends UserData = UserData,
> = {
  group?: BaseSearchConfig<G>;
  post?: BaseSearchConfig<P>;
  topic?: BaseSearchConfig<T>;
  user?: BaseSearchConfig<U> & { includeDeactivated?: boolean };
  order?: LocalEntitySearchType[];
};

type ToEntity<Data extends BaseData, T extends LocalEntitySearchType> = (
  object: Data,
  options?: {
    score?: number;
    matches?: ReturnType<SearchKeyMatcher<Data>>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extra?: (obj: Data) => any;
  },
) => BaseEntity<Data, T>;

type SearchKeyMatcher<Data extends BaseData> = (
  tokens: string[],
  item: Data,
  key: string,
) => readonly Fuse.FuseResultMatch[];

// Use consistent main field weight across types to normalize scores
enum FIELD_WEIGHT {
  Main = 0.9999,
  Secondary = 0.00009,
  Tertiary = 1 - FIELD_WEIGHT.Main - FIELD_WEIGHT.Secondary,
}

type SearchKeyOptions = {
  name: string;
  weight: FIELD_WEIGHT;
};

interface SearchOptions extends Fuse.IFuseOptions<unknown> {
  keys: SearchKeyOptions[];
}

type Indices = [number, number][];

const removeOverlaps: (indices: Indices) => Indices = (indices) =>
  indices
    .sort((left, right) => left[0] - right[0])
    .reduce((memo, [start, end]) => {
      const prevEnd = memo.length > 0 ? memo[memo.length - 1]?.[1] ?? -1 : -1;
      if (end > prevEnd) {
        const newStart = Math.max(prevEnd + 1, start);
        memo.push([newStart, end]);
      }

      return memo;
    }, [] as Indices);

const mergeMatches =
  (tokens: string[]) =>
  <T extends BaseData>(result: Fuse.FuseResult<T>): Fuse.FuseResult<T> => {
    return {
      ...result,
      matches: Object.values(
        _.groupBy(result.matches, (match) =>
          [match.key, match.value, match.refIndex].join('/'),
        ),
      )
        .map((items) => {
          return items.reduce(
            (merged, item) => ({
              ...item,
              indices: [...merged.indices, ...item.indices],
            }),
            { indices: [] } as Fuse.FuseResultMatch,
          );
        })
        .map((item) => {
          // `indices` returned by Fuse.js does not respect threshold,
          // so if there are exact matches, we require all indices to exactly match at least one token.
          // if there are not, it means all results are approximate, in which case we do nothing.
          // https://github.com/krisk/Fuse/issues/611
          const exactMatches = item.indices.filter((indice) => {
            const value = item.value
              ?.substring(indice[0], indice[1] + 1)
              .toLowerCase();
            return value && tokens.some((token) => token === value);
          });

          return {
            ...item,
            indices: exactMatches.length
              ? removeOverlaps(exactMatches)
              : removeOverlaps([...item.indices]),
          };
        }),
    };
  };

// By default, the shorter the field, the higher its relevance.
// However, that does not make sense when searching people's names.
// In that case, we should disable `ignoreFieldNorm` to let fuzziness score
// decide the relevance.
const getSearchOptions: (keys: SearchKeyOptions[]) => SearchOptions = (
  keys,
) => {
  const remainingWeight = 1 - _.sumBy(keys, 'weight');
  const normaizedKeys =
    remainingWeight > 0
      ? [...keys, { name: '__normalization_property', weight: remainingWeight }]
      : keys;
  return {
    keys: normaizedKeys,
    distance: DISTANCE,
    includeScore: true,
    fieldNormWeight: 0.01,
    includeMatches: true,
    threshold: THRESHOLD,
    useExtendedSearch: true,
  };
};

const cachedFuse = new WeakMap<
  BaseData[],
  { options: Fuse.IFuseOptions<unknown>; fuse: Fuse<unknown> }[]
>();

const getFuse = <Data extends BaseData>(
  objects: Data[],
  options: Fuse.IFuseOptions<Data>,
): Fuse<Data> => {
  const caches = cachedFuse.get(objects);
  const matchedFuse = caches?.find((cache) =>
    _.isEqual(cache.options, options),
  );
  if (matchedFuse) {
    return matchedFuse.fuse as Fuse<Data>;
  }
  const fuse = new Fuse(objects, options);
  cachedFuse.set(objects, [
    { options: options as Fuse.IFuseOptions<unknown>, fuse },
    ...(caches || []),
  ]);
  return fuse;
};

const fuseSearch = <Data extends BaseData>(
  objects: Data[],
  options: SearchOptions,
  tokens: string[],
) => {
  const results = getFuse(objects, options)
    .search({
      $and: tokens.map((searchToken, index) => {
        const orFields: Fuse.Expression[] = options.keys.map(({ name }) => ({
          // Prefer prefix match with fuzzy match + location === 0
          [name]: index === 0 ? searchToken : `'${searchToken}`,
        }));

        return {
          $or: orFields,
        };
      }),
    })
    .map(mergeMatches(tokens));

  return results;
};

type ToEntityOptions<
  Data extends BaseData,
  ExtraData extends object = Record<string, unknown>,
> = {
  score?: number;
  matches?: ReturnType<SearchKeyMatcher<Data>>;
  extra?: (obj: Data) => ExtraData | undefined;
};

const baseToEntity = <
  Data extends BaseData & { [name in NameKey]: string },
  T extends LocalEntitySearchType,
  NameKey extends string,
  ExtraData extends object,
>(
  type: T,
  nameKey: keyof Data,
  object: Data,
  {
    score = 0,
    matches = [],
    extra = () => undefined,
  }: ToEntityOptions<Data, ExtraData> = {},
): BaseEntity<Data, T> & ExtraData => ({
  ...({ [type]: object } as { [key in T]: Data }),
  ...(extra(object) as ExtraData),
  id: object.id,
  name: object[nameKey],
  type,
  object,
  score,
  matches,
});

export const groupToEntity = <G extends Pick<GroupData, 'id' | 'name'>>(
  group: G,
  options?: ToEntityOptions<G>,
) => baseToEntity('group', 'name', group, options);
export const postToEntity = <P extends Pick<PostData, 'id' | 'title'>>(
  post: P,
  options?: ToEntityOptions<P>,
) => baseToEntity('post', 'title', post, options);
export const topicToEntity = <T extends Pick<TopicData, 'id' | 'name'>>(
  topic: T,
  options?: ToEntityOptions<T>,
) => baseToEntity('topic', 'name', topic, options);
export const userToEntity = <U extends Pick<UserData, 'id' | 'name'>>(
  user: U,
  options?: ToEntityOptions<U>,
) => baseToEntity('user', 'name', user, options);

const BYPASS_SORT = () => 0;

const singleEntitySearch = <
  Data extends BaseData,
  T extends LocalEntitySearchType,
>(
  tokens: string[],
  options: SearchOptions,
  { extra, filter, objects, sortBy = BYPASS_SORT }: BaseSearchConfig<Data>,
  {
    tiebreaker,
    toEntity,
  }: {
    tiebreaker: (a: Data, b: Data) => number;
    toEntity: ToEntity<Data, T>;
  },
) => {
  let searchResults: Fuse.FuseResult<Data>[] =
    tokens.length > 0
      ? fuseSearch<Data>(objects, options, tokens)
      : objects.map((obj, index) => ({
          item: obj,
          matches: undefined,
          score: undefined,
          refIndex: index,
        }));

  // Apply filter after we got results to avoid recreating Fuse's index
  // every time we do the search.
  if (filter) {
    searchResults = searchResults.filter((result) => filter(result.item));
  }

  return searchResults
    .sort(({ item: l }, { item: r }) => sortBy(l, r) || tiebreaker(l, r))
    .map(({ item, matches, score }) =>
      toEntity(item, { score, matches, extra }),
    );
};

export enum GroupMatchKey {
  Name = 'name',
}
const groupSearch = <G extends GroupData>(
  tokens: string[],
  space: BaseSearchConfig<G>,
) => {
  const tiebreaker = (g1: G, g2: G) =>
    g1.name.toUpperCase().localeCompare(g2.name.toUpperCase());
  const options = getSearchOptions([
    { name: GroupMatchKey.Name, weight: FIELD_WEIGHT.Main },
  ]);

  return singleEntitySearch<G, 'group'>(tokens, options, space, {
    tiebreaker,
    toEntity: groupToEntity,
  });
};

export enum PostMatchKey {
  Title = 'title',
  TopicNames = 'topics.name',
  AncestorTopicNames = 'topics.ancestors.name',
}
const postSearch = <P extends PostData>(
  tokens: string[],
  space: BaseSearchConfig<P>,
) => {
  const tiebreaker = (p1: P, p2: P) => {
    if (p1.archivedAt && p2.archivedAt) {
      return (
        new Date(p2.archivedAt).getTime() - new Date(p1.archivedAt).getTime()
      );
    }
    return p1.archivedAt ? 1 : -1;
  };
  const options = getSearchOptions([
    { name: PostMatchKey.Title, weight: FIELD_WEIGHT.Main },
    {
      name: PostMatchKey.TopicNames,
      weight: FIELD_WEIGHT.Secondary,
    },
    {
      name: PostMatchKey.AncestorTopicNames,
      weight: FIELD_WEIGHT.Tertiary,
    },
  ]);

  return singleEntitySearch<P, 'post'>(tokens, options, space, {
    tiebreaker,
    toEntity: postToEntity,
  });
};

export enum TopicMatchKey {
  Name = 'name',
  AncestorNames = 'ancestors.name',
}
const topicSearch = <T extends TopicData>(
  tokens: string[],
  space: BaseSearchConfig<T>,
) => {
  const tiebreaker = (t1: T, t2: T) => (t2.score || 0) - (t1.score || 0);
  const options = getSearchOptions([
    { name: TopicMatchKey.Name, weight: FIELD_WEIGHT.Main },
    { name: TopicMatchKey.AncestorNames, weight: FIELD_WEIGHT.Secondary },
  ]);

  return singleEntitySearch<T, 'topic'>(tokens, options, space, {
    tiebreaker,
    toEntity: topicToEntity,
  });
};

export enum UserMatchKey {
  Name = 'name',
  Email = 'email',
  Title = 'title',
}
const userSearch = <U extends UserData>(
  tokens: string[],
  space: BaseSearchConfig<U>,
) => {
  const tiebreaker = (u1: U, u2: U) =>
    u1.name.toUpperCase().localeCompare(u2.name.toUpperCase());

  const isSearchingEmail = tokens.length === 1 && tokens[0]?.includes('@');
  const options = getSearchOptions(
    isSearchingEmail
      ? [{ name: UserMatchKey.Email, weight: FIELD_WEIGHT.Main }]
      : [
          { name: UserMatchKey.Name, weight: FIELD_WEIGHT.Main },
          { name: UserMatchKey.Title, weight: FIELD_WEIGHT.Secondary },
          { name: UserMatchKey.Email, weight: FIELD_WEIGHT.Tertiary },
        ],
  );

  return singleEntitySearch<U, 'user'>(tokens, options, space, {
    tiebreaker,
    toEntity: userToEntity,
  });
};

function localEntitySearch<
  G extends GroupData = GroupData,
  P extends PostData = PostData,
  T extends TopicData = TopicData,
  U extends UserData = UserData,
>(
  query: string,
  config: LocalEntitySearchConfig<G, P, T, U>,
  options?:
    | { limit: number; limitPerType?: never }
    | { limit?: never; limitPerType: number },
): (GroupEntity<G> | PostEntity<P> | TopicEntity<T> | UserEntity<U>)[] {
  const limit = options?.limit ?? 100;
  const limitPerType = options?.limitPerType;

  const trimmedQuery = query.trim();
  const tokens = trimmedQuery ? trimmedQuery.toLowerCase().split(/\s+/) : [];
  const { order: specifiedOrder, ...entityConfigs } = config;
  const order =
    specifiedOrder || (Object.keys(entityConfigs) as LocalEntitySearchType[]);

  const byScore = (
    { score: s1 }: { score: number },
    { score: s2 }: { score: number },
  ) => s1 - s2;

  const results = order
    .map((type) => {
      if (!entityConfigs[type]) {
        return [];
      }

      switch (type) {
        case 'group':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return groupSearch<G>(tokens, entityConfigs[type]!);
        case 'post':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return postSearch<P>(tokens, entityConfigs[type]!);
        case 'topic':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return topicSearch<T>(tokens, entityConfigs[type]!);
        case 'user':
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return userSearch<U>(tokens, entityConfigs[type]!);
        default:
          return [];
      }
    })
    .map((typeResults) =>
      limitPerType
        ? typeResults.sort(byScore).slice(0, limitPerType)
        : typeResults,
    )
    .flat();

  const sortedResults = query ? results.sort(byScore) : results;
  return limitPerType ? sortedResults : sortedResults.slice(0, limit);
}

// When working with nested arrays, `refIndex` represents the index of an item
// in the flatten array.
// For example, given `[{ ancestors: ["A", "B"] }, { ancestors: ["C"] }]`,
// the `refIndex` of `"C"` is 2.
// This function is used to get the innerIndex (0 in the example) and the outerIndex (1 in the example)
// of a given `refIndex`.
export function unflattenRefIndex<
  K extends string,
  T extends { [key in K]?: unknown[] },
>(
  items: T[],
  field: K,
  refIndex: number,
): { innerIndex: number; outerIndex: number } {
  let innerIndex = refIndex;
  const outerIndex = items.findIndex((item) => {
    const innerLength = item[field]?.length ?? 0;

    if (innerIndex < innerLength) {
      return true;
    }

    innerIndex -= innerLength;
    return false;
  });

  return { innerIndex, outerIndex };
}

export default localEntitySearch;
